import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import {
  MdEdit,
  MdInfoOutline,
  MdPowerSettingsNew,
  MdRefresh,
  MdRestartAlt,
  MdSettings,
  MdTv,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import useHeader from 'containers/App/Header/useHeader';
import {
  AdminPod,
  AdminPodActionAuditUpdate,
  AdminPodActionLog,
  AdminPodInventoryResponse,
  useCreateAdminPodActionAuditMutation,
  useGetAdminPodActionLogsQuery,
  useGetAdminPodsQuery,
  useLazyReadAdminPodConfigurationQuery,
  useReloadAdminPodVideoMutation,
  useRestartAdminPodProcessMutation,
  useRestartAdminPodPuppeteerMutation,
  useUpdateAdminPodConfigurationMutation,
  useUpdateAdminPodActionAuditMutation,
  useUpdateAdminPodSelectionStatusMutation,
  useWakeupAdminPodMutation,
} from 'services/api/adminPodManagement';
import {
  useAdsureMonitoringPodsQuery,
  useLazyReadCreativeAdminPodConfigurationQuery,
  useRestartCreativeAdminPodProcessMutation,
  useUpdateAdsurePodStatusMutation,
  useUpdateCreativeAdminPodConfigurationMutation,
  useWakeupCreativeAdminPodMutation,
} from 'services/api/creativeServer';
import {
  PodManagementModalGlobalStyles,
  PodManagementPageStyles,
} from './PodManagementPageStyles';

type ConfigForm = {
  os: string;
  osv: string;
  region: string;
  regionCode: string;
  chipset: string;
  supportedCountries: string;
  adSlots: string;
  testSources: string;
};

type PodConfigurationPayload = {
  pod?: string;
  boardIndex?: number | null;
  editableFields?: {
    metadata?: {
      supportedCountries?: string[];
      adSlots?: string[];
      testSources?: string[];
    };
  };
  summary?: {
    server?: string;
    setup?: string;
    webRTCPort?: number | null;
    control?: string;
    metadata?: Record<string, unknown>;
  };
};

type PodActionModalPod = AdminPod & {
  api: {
    refreshPods: () => unknown;
    wakeup: () => Promise<unknown>;
    reloadVideo: () => Promise<unknown>;
    restartPuppeteer: () => Promise<unknown>;
    restartPodProcess: () => Promise<unknown>;
    updateSelectionStatus: () => Promise<unknown>;
  };
};

const EMPTY_CONFIG_FORM: ConfigForm = {
  os: '',
  osv: '',
  region: '',
  regionCode: '',
  chipset: '',
  supportedCountries: '',
  adSlots: '',
  testSources: '',
};

const EMPTY_POD_INVENTORY: AdminPodInventoryResponse = {
  pods: [],
  partialErrors: [],
};

const POD_NAME_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const getErrorMessage = (error: any, fallback: string) =>
  String(
    error?.data?.error ||
      error?.data?.message ||
      error?.error ||
      error?.message ||
      fallback,
  );

const getActionData = (response: any) =>
  response?.data?.result?.data || response?.data?.data || response?.data || {};

const formatTimestamp = (value?: string | number | null) => {
  if (!value) return 'NA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'NA';
  return date.toLocaleString();
};

const parseList = (value: string) =>
  Array.from(
    new Set(
      value
        .split(/\r?\n|,/)
        .map(item => item.trim())
        .filter(Boolean),
    ),
  );

const listToText = (value?: string[]) => (value || []).join('\n');

const getPodTypeLabel = (pod: AdminPod) => {
  const metadata = pod.metadata || {};
  if (metadata.podType === 'automation') return 'Automation';
  if (Array.isArray(metadata.testSources)) return 'Automation';
  if (pod.name.includes('creative-testing')) return 'Automation';
  return 'Self-Serve';
};

const isCreativeTestingPod = (pod: AdminPod | null) =>
  Boolean(
    pod &&
      (pod.name.includes('creative-testing') ||
        pod.controlPlane === 'creative-testing' ||
        pod.source === 'creative-testing' ||
        pod.metadata?.podType === 'automation' ||
        Array.isArray(pod.metadata?.testSources)),
  );

const getMetadataString = (
  metadata: Record<string, unknown>,
  field: string,
) => {
  const value = metadata[field];
  if (typeof value === 'undefined' || value === null) return '';
  return String(value);
};

const getStatusVariant = (isActive: boolean) =>
  isActive ? 'success' : 'secondary';

const getCreativeCapabilityIds = (podRecord: any) => {
  const capabilities = Array.isArray(podRecord?.podCommandCapabilities)
    ? podRecord.podCommandCapabilities
    : podRecord?.capabilities;

  if (!Array.isArray(capabilities)) return [];

  return capabilities
    .map(capability =>
      typeof capability === 'string' ? capability : capability?.id,
    )
    .filter(Boolean);
};

const normalizeCreativeMonitoringPods = (podRecords: any): AdminPod[] =>
  Object.entries(podRecords || {})
    .map(([podName, podRecord]: [string, any]) => {
      const metadata = podRecord?.metadata || {};
      const capabilities = getCreativeCapabilityIds(podRecord);
      const isOnline = true;
      const hasCapability = (capability: string) =>
        capabilities.includes(capability);

      return {
        name: podName,
        source: 'creative-testing' as const,
        controlPlane: 'creative-testing' as const,
        isOnline,
        isReady: isOnline,
        connectionStatus: isOnline ? 'active' : 'inactive',
        debugConnectionStatus: isOnline ? 'ACTIVE' : 'INACTIVE',
        isTvOnline: !podRecord?.lastAlerted,
        isVideoOnline: false,
        isPodAvailable:
          typeof podRecord?.isPodAvailable === 'boolean'
            ? podRecord.isPodAvailable
            : metadata.isPodAvailable,
        activeClients: [],
        lastActive: 'N.A.',
        lastActiveAt: null,
        lastUpdatedAt: podRecord?.lastUpdated || null,
        remoteAddress: podRecord?.remoteAddress,
        metadata,
        capabilities,
        supportedActions: {
          wakeup: isOnline,
          videoReload: false,
          restartPuppeteer: false,
          restartPodProcess: hasCapability('pod-process-restart'),
          readConfiguration: hasCapability('configuration-read'),
          updateConfiguration: hasCapability('configuration-update'),
          pauseSelection:
            isOnline &&
            (metadata.podType === 'automation' ||
              Array.isArray(metadata.testSources) ||
              podName.includes('creative-testing')),
          smartifiSwitch: false,
        },
        smartifi: {
          supported: false,
          switchId: metadata.smartifiSwitchId || metadata.smartifiDeviceId || '',
          state: metadata.smartifiState || metadata.smartifiPowerState || '',
        },
      };
    })
    .filter(pod => pod.name);

const getLogStatusVariant = (status: string) => {
  if (status === 'requested') return 'secondary';
  if (status === 'success') return 'success';
  if (status === 'unsupported') return 'warning';
  return 'danger';
};

const DISABLED_ACTION_TOOLTIP = 'This action is not supported by this pod';

function ActionInfoTooltip() {
  return (
    <OverlayTrigger
      placement="left"
      trigger="click"
      rootClose
      overlay={
        <Tooltip id="pod-management-action-tooltip">
          <div className="pod-management-tooltip-list">
            <div>
              <strong>Wakeup:</strong> Powers on the TV.
            </div>
            <div>
              <strong>Video:</strong> Refreshes the video stream.
            </div>
            <div>
              <strong>PM2:</strong> Restarts Puppeteer on self-serve pods.
            </div>
            <div>
              <strong>Pod:</strong> Restarts the pod process.
            </div>
            <div>
              <strong>Config:</strong> Edits supported configuration fields.
            </div>
            <div>
              <strong>Pause:</strong> Prevents new creative test selection.
            </div>
          </div>
        </Tooltip>
      }
    >
      <Button
        variant="link"
        size="sm"
        className="pod-management-info-button"
        aria-label="Show action descriptions"
      >
        <MdInfoOutline />
      </Button>
    </OverlayTrigger>
  );
}

function ConfigEditorModal({
  pod,
  configuration,
  form,
  isLoading,
  isSaving,
  show,
  onChange,
  onHide,
  onSave,
}: {
  pod: AdminPod | null;
  configuration: PodConfigurationPayload | null;
  form: ConfigForm;
  isLoading: boolean;
  isSaving: boolean;
  show: boolean;
  onChange: (field: keyof ConfigForm, value: string) => void;
  onHide: () => void;
  onSave: () => void;
}) {
  const summary = configuration?.summary || {};
  const metadata = summary.metadata || {};
  const isCreativePod = isCreativeTestingPod(pod);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      dialogClassName="pod-management-config-dialog"
    >
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>Pod Configuration</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!pod ? null : (
          <div className="pod-management-config-summary">
            <h4>{pod.name}</h4>
            <dl>
              <dt>Board</dt>
              <dd>{configuration?.boardIndex || 'NA'}</dd>
              <dt>Type</dt>
              <dd>{getPodTypeLabel(pod)}</dd>
              <dt>OS</dt>
              <dd>
                {getMetadataString(metadata, 'os') || 'webOS'}{' '}
                {getMetadataString(metadata, 'osv') || 'NA'}
              </dd>
              <dt>Region</dt>
              <dd>
                {getMetadataString(metadata, 'region') || 'NA'} /{' '}
                {getMetadataString(metadata, 'regionCode') || 'NA'}
              </dd>
              <dt>Server</dt>
              <dd>{summary.server || 'NA'}</dd>
            </dl>
          </div>
        )}

        {isLoading ? (
          <div className="pod-management-loading-state">
            <Spinner animation="border" className="me-2" />
            Loading configuration...
          </div>
        ) : (
          <Row className="g-3">
            <Col xs={12}>
              <div className="pod-management-config-section">
                <div className="pod-management-config-section-header">
                  <div>
                    <h5>Device Metadata</h5>
                  </div>
                </div>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label className="pod-management-label">OS</Form.Label>
                    <Form.Control
                      value={form.os}
                      onChange={event => onChange('os', event.target.value)}
                      placeholder="webOS"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="pod-management-label">
                      OS Version
                    </Form.Label>
                    <Form.Control
                      value={form.osv}
                      onChange={event => onChange('osv', event.target.value)}
                      placeholder="26"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label className="pod-management-label">
                      Chipset
                    </Form.Label>
                    <Form.Control
                      value={form.chipset}
                      onChange={event =>
                        onChange('chipset', event.target.value)
                      }
                      placeholder="K25LPNLM"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="pod-management-label">
                      Region
                    </Form.Label>
                    <Form.Control
                      value={form.region}
                      onChange={event => onChange('region', event.target.value)}
                      placeholder="US"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="pod-management-label">
                      Region Code
                    </Form.Label>
                    <Form.Control
                      value={form.regionCode}
                      onChange={event =>
                        onChange('regionCode', event.target.value)
                      }
                      placeholder="ATSC"
                    />
                  </Col>
                </Row>
              </div>
            </Col>
            {isCreativePod ? (
              <Col xs={12}>
                <div className="pod-management-config-section">
                  <div className="pod-management-config-section-header">
                    <div>
                      <h5>Creative Test Selection</h5>
                    </div>
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Label className="pod-management-label">
                        Supported Countries
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        className="pod-management-textarea"
                        value={form.supportedCountries}
                        onChange={event =>
                          onChange('supportedCountries', event.target.value)
                        }
                        placeholder="GBR&#10;IND&#10;USA"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label className="pod-management-label">
                        Test Sources
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        className="pod-management-textarea"
                        value={form.testSources}
                        onChange={event =>
                          onChange('testSources', event.target.value)
                        }
                        placeholder="programmatic&#10;on-demand"
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label className="pod-management-label">
                        Ad Slots
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        className="pod-management-textarea pod-management-textarea-large"
                        value={form.adSlots}
                        onChange={event =>
                          onChange('adSlots', event.target.value)
                        }
                        placeholder="Portal/HomeBigCarousel@Main&#10;WebURLTester@Main"
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            ) : null}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          className="pod-management-primary-button"
          variant="primary"
          onClick={onSave}
          disabled={isSaving || isLoading || !pod}
        >
          {isSaving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving
            </>
          ) : (
            'Save And Restart'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function AuditLogItem({ log }: { log: AdminPodActionLog }) {
  const actor = log.actor?.email || log.actor?.name || log.actor?.id || 'Admin';

  return (
    <div className="pod-management-log-item">
      <div className="pod-management-log-topline">
        <div className="pod-management-log-action">{log.action}</div>
        <Badge bg={getLogStatusVariant(log.status)}>{log.status}</Badge>
      </div>
      <div className="pod-management-log-meta">{log.pod}</div>
      <div className="pod-management-log-meta">
        {actor} - {formatTimestamp(log.createdAt)}
      </div>
      {log.error ? (
        <div className="pod-management-log-meta text-danger">{log.error}</div>
      ) : null}
    </div>
  );
}

function PodActionButton({
  children,
  disabled,
  unsupported,
  onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  unsupported: boolean;
  onClick: () => void;
}) {
  const button = (
    <Button
      variant="primary"
      className="pod-management-modal-action"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );

  if (!unsupported) return button;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="pod-management-disabled-action-tooltip">
          {DISABLED_ACTION_TOOLTIP}
        </Tooltip>
      }
    >
      <span className="pod-management-disabled-action-wrapper">{button}</span>
    </OverlayTrigger>
  );
}

function PodActionsModal({
  pod,
  activeAction,
  show,
  onHide,
  onRunAction,
  onOpenConfiguration,
}: {
  pod: PodActionModalPod | null;
  activeAction: string;
  show: boolean;
  onHide: () => void;
  onRunAction: (params: {
    key: string;
    pod: AdminPod;
    label: string;
    action: () => Promise<unknown>;
    afterSuccess?: () => void;
  }) => void;
  onOpenConfiguration: (pod: AdminPod) => void;
}) {
  if (!pod) return null;

  const isBusy = Boolean(activeAction);
  const actionBusyPrefix = (key: string) =>
    activeAction === `${key}:${pod.name}`;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="pod-management-actions-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>{pod.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pod-management-modal-actions">
          <PodActionButton
            disabled={!pod.supportedActions.wakeup || isBusy}
            unsupported={!pod.supportedActions.wakeup}
            onClick={() =>
              onRunAction({
                key: 'wakeup',
                pod,
                label: 'Wakeup',
                action: () => pod.api.wakeup(),
              })
            }
          >
            {actionBusyPrefix('wakeup') ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <MdPowerSettingsNew />
            )}
            Wakeup
          </PodActionButton>
          <PodActionButton
            disabled={!pod.supportedActions.videoReload || isBusy}
            unsupported={!pod.supportedActions.videoReload}
            onClick={() =>
              onRunAction({
                key: 'video',
                pod,
                label: 'Video reload',
                action: () => pod.api.reloadVideo(),
                afterSuccess: () => {
                  pod.api.refreshPods();
                  window.setTimeout(() => pod.api.refreshPods(), 4000);
                },
              })
            }
          >
            {actionBusyPrefix('video') ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <MdTv />
            )}
            Video
          </PodActionButton>
          <PodActionButton
            disabled={!pod.supportedActions.restartPuppeteer || isBusy}
            unsupported={!pod.supportedActions.restartPuppeteer}
            onClick={() =>
              onRunAction({
                key: 'pm2',
                pod,
                label: 'Puppeteer restart',
                action: () => pod.api.restartPuppeteer(),
              })
            }
          >
            {actionBusyPrefix('pm2') ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <MdRestartAlt />
            )}
            PM2
          </PodActionButton>
          <PodActionButton
            disabled={!pod.supportedActions.restartPodProcess || isBusy}
            unsupported={!pod.supportedActions.restartPodProcess}
            onClick={() =>
              onRunAction({
                key: 'pod-process',
                pod,
                label: 'Pod process restart',
                action: () => pod.api.restartPodProcess(),
              })
            }
          >
            {actionBusyPrefix('pod-process') ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <MdRestartAlt />
            )}
            Pod
          </PodActionButton>
          <PodActionButton
            disabled={!pod.supportedActions.readConfiguration || isBusy}
            unsupported={!pod.supportedActions.readConfiguration}
            onClick={() => {
              onHide();
              onOpenConfiguration(pod);
            }}
          >
            <MdEdit />
            Config
          </PodActionButton>
          <PodActionButton
            disabled={!pod.supportedActions.pauseSelection || isBusy}
            unsupported={!pod.supportedActions.pauseSelection}
            onClick={() =>
              onRunAction({
                key: 'status',
                pod,
                label:
                  pod.isPodAvailable === false
                    ? 'Resume selection'
                    : 'Pause selection',
                action: () => pod.api.updateSelectionStatus(),
                afterSuccess: () => pod.api.refreshPods(),
              })
            }
          >
            <MdSettings />
            {pod.isPodAvailable === false ? 'Resume' : 'Pause'}
          </PodActionButton>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default function PodManagementPage() {
  useHeader('Pod Management');

  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [auditPodFilter, setAuditPodFilter] = React.useState('');
  const [activeAction, setActiveAction] = React.useState('');
  const [actionsPodName, setActionsPodName] = React.useState('');
  const [configPod, setConfigPod] = React.useState<AdminPod | null>(null);
  const [configForm, setConfigForm] =
    React.useState<ConfigForm>(EMPTY_CONFIG_FORM);
  const [configPayload, setConfigPayload] =
    React.useState<PodConfigurationPayload | null>(null);

  const {
    data: podInventory = EMPTY_POD_INVENTORY,
    error: podsError,
    isFetching: podsFetching,
    isLoading: podsLoading,
    refetch: refetchPods,
  } = useGetAdminPodsQuery();
  const {
    data: creativePodRecords = {},
    error: creativePodsError,
    isFetching: creativePodsFetching,
    isLoading: creativePodsLoading,
    refetch: refetchCreativePods,
  } = useAdsureMonitoringPodsQuery(undefined);
  const creativePods = React.useMemo(
    () => normalizeCreativeMonitoringPods(creativePodRecords),
    [creativePodRecords],
  );
  const pods = React.useMemo(() => {
    const podsByName = new Map<string, AdminPod>();

    podInventory.pods.forEach(pod => podsByName.set(pod.name, pod));
    creativePods.forEach(pod => {
      if (!podsByName.has(pod.name)) podsByName.set(pod.name, pod);
    });

    return Array.from(podsByName.values()).sort((left, right) =>
      POD_NAME_COLLATOR.compare(left.name, right.name),
    );
  }, [creativePods, podInventory.pods]);
  const podInventoryWarnings = React.useMemo(() => {
    const warnings = [...podInventory.partialErrors];

    if (creativePodsError) {
      warnings.push({
        source: 'creative-testing',
        message: 'Creative testing pod inventory is unavailable.',
        detail: getErrorMessage(
          creativePodsError,
          'Unable to load creative testing pods.',
        ),
      });
    }

    return warnings;
  }, [creativePodsError, podInventory.partialErrors]);
  const {
    data: auditLogs = [],
    error: auditError,
    isFetching: auditFetching,
  } = useGetAdminPodActionLogsQuery({
    ...(auditPodFilter ? { pod: auditPodFilter } : {}),
    limit: 20,
  });
  const [readConfiguration, readConfigurationState] =
    useLazyReadAdminPodConfigurationQuery();
  const [updateConfiguration, updateConfigurationState] =
    useUpdateAdminPodConfigurationMutation();
  const [wakeupPod] = useWakeupAdminPodMutation();
  const [reloadVideo] = useReloadAdminPodVideoMutation();
  const [restartPuppeteer] = useRestartAdminPodPuppeteerMutation();
  const [restartPodProcess] = useRestartAdminPodProcessMutation();
  const [updateSelectionStatus] = useUpdateAdminPodSelectionStatusMutation();
  const [createActionAudit] = useCreateAdminPodActionAuditMutation();
  const [updateActionAudit] = useUpdateAdminPodActionAuditMutation();
  const [readCreativeConfiguration, readCreativeConfigurationState] =
    useLazyReadCreativeAdminPodConfigurationQuery();
  const [updateCreativeConfiguration, updateCreativeConfigurationState] =
    useUpdateCreativeAdminPodConfigurationMutation();
  const [restartCreativePodProcess] = useRestartCreativeAdminPodProcessMutation();
  const [wakeupCreativePod] = useWakeupCreativeAdminPodMutation();
  const [updateCreativeSelectionStatus] = useUpdateAdsurePodStatusMutation();
  const isPodInventoryFetching = podsFetching || creativePodsFetching;
  const isPodInventoryLoading = podsLoading || creativePodsLoading;
  const refreshPodInventory = React.useCallback(() => {
    refetchPods();
    refetchCreativePods();
  }, [refetchCreativePods, refetchPods]);

  const runAuditedCreativeAction = React.useCallback(
    async ({
      pod,
      action,
      request = {},
      run,
    }: {
      pod: string;
      action: string;
      request?: Record<string, unknown>;
      run: () => Promise<unknown>;
    }) => {
      const audit = await createActionAudit({
        pod,
        action,
        request: {
          controlPlane: 'creative-testing',
          ...request,
        },
      }).unwrap();

      try {
        const response = await run();
        await updateActionAudit({
          id: audit.id,
          pod,
          action,
          status: 'success',
          response: (response || {}) as AdminPodActionAuditUpdate['response'],
        }).unwrap();
        return response;
      } catch (error) {
        await updateActionAudit({
          id: audit.id,
          pod,
          action,
          status: 'failure',
          error: getErrorMessage(error, `${action} failed.`),
        }).unwrap();
        throw error;
      }
    },
    [createActionAudit, updateActionAudit],
  );

  const metrics = React.useMemo(() => {
    const online = pods.filter(pod => pod.isOnline).length;
    const videoOnline = pods.filter(pod => pod.isVideoOnline).length;
    const configurable = pods.filter(
      pod => pod.supportedActions.readConfiguration,
    ).length;

    return {
      total: pods.length,
      online,
      videoOnline,
      configurable,
    };
  }, [pods]);

  const filteredPods = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return pods.filter(pod => {
      const type = getPodTypeLabel(pod);
      const matchesSearch =
        !normalizedSearch ||
        pod.name.toLowerCase().includes(normalizedSearch) ||
        String(pod.metadata?.region || '')
          .toLowerCase()
          .includes(normalizedSearch) ||
        String(pod.metadata?.regionCode || '')
          .toLowerCase()
          .includes(normalizedSearch);
      const matchesType =
        typeFilter === 'all' || type.toLowerCase() === typeFilter;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'online' && pod.isOnline) ||
        (statusFilter === 'offline' && !pod.isOnline);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [pods, search, statusFilter, typeFilter]);

  const selectedActionsPod = React.useMemo<PodActionModalPod | null>(() => {
    const pod = pods.find(candidate => candidate.name === actionsPodName);
    if (!pod) return null;
    const isCreativePod = isCreativeTestingPod(pod);

    return {
      ...pod,
      api: {
        refreshPods: refreshPodInventory,
        wakeup: () =>
          isCreativePod
            ? runAuditedCreativeAction({
                pod: pod.name,
                action: 'wakeup',
                run: () => wakeupCreativePod({ pod: pod.name }).unwrap(),
              })
            : wakeupPod({ pod: pod.name }).unwrap(),
        reloadVideo: () => reloadVideo(pod.name).unwrap(),
        restartPuppeteer: () => restartPuppeteer({ pod: pod.name }).unwrap(),
        restartPodProcess: () =>
          isCreativePod
            ? runAuditedCreativeAction({
                pod: pod.name,
                action: 'pod-process-restart',
                run: () =>
                  restartCreativePodProcess({ pod: pod.name }).unwrap(),
              })
            : restartPodProcess({ pod: pod.name }).unwrap(),
        updateSelectionStatus: () =>
          isCreativePod
            ? runAuditedCreativeAction({
                pod: pod.name,
                action: 'pod-selection-status',
                request: { enabled: pod.isPodAvailable === false },
                run: () =>
                  updateCreativeSelectionStatus({
                    pod: pod.name,
                    status: pod.isPodAvailable === false,
                  }).unwrap(),
              })
            : updateSelectionStatus({
                pod: pod.name,
                enabled: pod.isPodAvailable === false,
              }).unwrap(),
      },
    };
  }, [
    actionsPodName,
    pods,
    refreshPodInventory,
    reloadVideo,
    restartCreativePodProcess,
    restartPodProcess,
    restartPuppeteer,
    runAuditedCreativeAction,
    updateCreativeSelectionStatus,
    updateSelectionStatus,
    wakeupCreativePod,
    wakeupPod,
  ]);

  const runAction = async ({
    key,
    pod,
    label,
    action,
    afterSuccess,
  }: {
    key: string;
    pod: AdminPod;
    label: string;
    action: () => Promise<unknown>;
    afterSuccess?: () => void;
  }) => {
    const actionKey = `${key}:${pod.name}`;

    try {
      setActiveAction(actionKey);
      await action();
      toast.success(`${label} requested for ${pod.name}.`);
      afterSuccess?.();
    } catch (error) {
      toast.error(getErrorMessage(error, `Unable to run ${label}.`));
    } finally {
      setActiveAction('');
    }
  };

  const openConfiguration = async (pod: AdminPod) => {
    try {
      setConfigPod(pod);
      setConfigPayload(null);
      setConfigForm(EMPTY_CONFIG_FORM);

      const response = isCreativeTestingPod(pod)
        ? await runAuditedCreativeAction({
            pod: pod.name,
            action: 'configuration-read',
            run: () => readCreativeConfiguration(pod.name).unwrap(),
          })
        : await readConfiguration(pod.name).unwrap();
      const config = getActionData(response) as PodConfigurationPayload;
      const metadata = config.editableFields?.metadata || {};
      const summaryMetadata = config.summary?.metadata || {};

      setConfigPayload(config);
      setConfigForm({
        os: getMetadataString(summaryMetadata, 'os'),
        osv: getMetadataString(summaryMetadata, 'osv'),
        region: getMetadataString(summaryMetadata, 'region'),
        regionCode: getMetadataString(summaryMetadata, 'regionCode'),
        chipset: getMetadataString(summaryMetadata, 'chipset'),
        supportedCountries: listToText(metadata.supportedCountries),
        adSlots: listToText(metadata.adSlots),
        testSources: listToText(metadata.testSources),
      });
    } catch (error) {
      setConfigPod(null);
      toast.error(
        getErrorMessage(error, 'Unable to load pod configuration right now.'),
      );
    }
  };

  const closeConfiguration = () => {
    if (updateConfigurationState.isLoading) return;
    setConfigPod(null);
    setConfigPayload(null);
    setConfigForm(EMPTY_CONFIG_FORM);
  };

  const saveConfiguration = async () => {
    if (!configPod) return;

    const metadataUpdate: Record<string, unknown> = {
      os: configForm.os.trim(),
      osv: configForm.osv.trim(),
      region: configForm.region.trim(),
      regionCode: configForm.regionCode.trim(),
      chipset: configForm.chipset.trim(),
    };

    if (isCreativeTestingPod(configPod)) {
      metadataUpdate.supportedCountries = parseList(
        configForm.supportedCountries,
      );
      metadataUpdate.adSlots = parseList(configForm.adSlots);
      metadataUpdate.testSources = parseList(configForm.testSources);
    }

    try {
      const configuration = {
        metadata: metadataUpdate,
      };

      if (isCreativeTestingPod(configPod)) {
        await runAuditedCreativeAction({
          pod: configPod.name,
          action: 'configuration-update',
          request: {
            configurationKeys: Object.keys(configuration),
            restartAfterUpdate: true,
          },
          run: () =>
            updateCreativeConfiguration({
              pod: configPod.name,
              restartAfterUpdate: true,
              configuration,
            }).unwrap(),
        });
      } else {
        await updateConfiguration({
          pod: configPod.name,
          restartAfterUpdate: true,
          configuration,
        }).unwrap();
      }

      toast.success(`Configuration saved for ${configPod.name}.`);
      closeConfiguration();
    } catch (error) {
      toast.error(
        getErrorMessage(error, 'Unable to update pod configuration right now.'),
      );
    }
  };

  const updateConfigField = (field: keyof ConfigForm, value: string) => {
    setConfigForm(current => ({
      ...current,
      [field]: value,
    }));
  };

  return (
    <>
      <PodManagementModalGlobalStyles />
      <PodManagementPageStyles>
        <div className="pod-management-shell">
        <div className="pod-management-header">
          <div>
            <div className="pod-management-eyebrow">Admin Center</div>
            <h2 className="pod-management-title">Pod Management</h2>
            <p className="pod-management-subtitle">
              Review pod availability, run remote recovery actions, and manage
              supported board-level configuration for connected TV lab devices.
            </p>
          </div>
          <div className="pod-management-metrics">
            <div className="pod-management-metric">
              <div className="pod-management-metric-value">{metrics.total}</div>
              <div className="pod-management-metric-label">
                {metrics.total === 1 ? 'Pod' : 'Pods'}
              </div>
            </div>
            <div className="pod-management-metric">
              <div className="pod-management-metric-value">
                {metrics.online}
              </div>
              <div className="pod-management-metric-label">Online</div>
            </div>
            <div className="pod-management-metric">
              <div className="pod-management-metric-value">
                {metrics.configurable}
              </div>
              <div className="pod-management-metric-label">Configurable</div>
            </div>
          </div>
        </div>

        {podInventoryWarnings.length ? (
          <Alert variant="warning" className="pod-management-partial-alert">
            {podInventoryWarnings.map(warning => (
              <div key={`${warning.source}-${warning.message}`}>
                <strong>{warning.message}</strong>
                {warning.detail ? ` ${warning.detail}` : ''}
              </div>
            ))}
          </Alert>
        ) : null}

        <div className="pod-management-filter-panel">
          <Row className="g-3 align-items-end">
            <Col lg={4}>
              <Form.Label className="pod-management-label">Search</Form.Label>
              <Form.Control
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Pod name, region, or region code"
              />
            </Col>
            <Col md={4} lg={3}>
              <Form.Label className="pod-management-label">Pod Type</Form.Label>
              <Form.Select
                value={typeFilter}
                onChange={event => setTypeFilter(event.target.value)}
              >
                <option value="all">All Types</option>
                <option value="self-serve">Self-Serve</option>
                <option value="automation">Automation</option>
              </Form.Select>
            </Col>
            <Col md={4} lg={3}>
              <Form.Label className="pod-management-label">Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={event => setStatusFilter(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </Form.Select>
            </Col>
            <Col md={4} lg={2}>
              <Button
                variant="outline-secondary"
                className="w-100 d-inline-flex align-items-center justify-content-center gap-2"
                onClick={refreshPodInventory}
                disabled={isPodInventoryFetching}
              >
                <MdRefresh />
                Refresh
              </Button>
            </Col>
          </Row>
        </div>

        <div className="pod-management-grid">
          <div className="pod-management-panel">
            <div className="pod-management-panel-header">
              <h3 className="pod-management-panel-title">
                Available Pods ({filteredPods.length})
              </h3>
              {isPodInventoryFetching ? (
                <span className="text-muted small">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Refreshing
                </span>
              ) : null}
            </div>

            {isPodInventoryLoading ? (
              <div className="pod-management-loading-state">
                <Spinner animation="border" className="me-2" />
                Loading pods...
              </div>
            ) : podsError ? (
              <Alert variant="danger" className="m-3">
                Unable to load pod management data.
              </Alert>
            ) : filteredPods.length === 0 ? (
              <div className="pod-management-empty-state">
                No pods matched the selected filters.
              </div>
            ) : (
              <div className="pod-management-table-scroll">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Pod</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Capabilities</th>
                      <th className="text-end pod-management-actions-column">
                        <span className="pod-management-actions-heading">
                          Actions
                          <ActionInfoTooltip />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPods.map(pod => {
                      const type = getPodTypeLabel(pod);

                      return (
                        <tr key={pod.name}>
                          <td>
                            <div className="pod-management-name">
                              {pod.name}
                            </div>
                            <div className="pod-management-meta">
                              {String(pod.metadata?.region || 'NA')} /{' '}
                              {String(pod.metadata?.regionCode || 'NA')} | WebOS{' '}
                              {String(pod.metadata?.osv || 'NA')}
                            </div>
                          </td>
                          <td>
                            <div className="pod-management-chip-row">
                              <Badge bg={getStatusVariant(pod.isOnline)}>
                                {pod.isOnline ? 'Online' : 'Offline'}
                              </Badge>
                              <Badge bg={getStatusVariant(pod.isVideoOnline)}>
                                {pod.isVideoOnline ? 'Video' : 'No Video'}
                              </Badge>
                            </div>
                          </td>
                          <td>
                            <Badge bg={type === 'Automation' ? 'info' : 'dark'}>
                              {type}
                            </Badge>
                          </td>
                          <td>
                            <div className="pod-management-chip-row">
                              {pod.supportedActions.readConfiguration ? (
                                <Badge bg="primary">Config</Badge>
                              ) : null}
                              {pod.supportedActions.restartPodProcess ? (
                                <Badge bg="primary">Pod Restart</Badge>
                              ) : null}
                              {pod.supportedActions.restartPuppeteer ? (
                                <Badge bg="secondary">PM2</Badge>
                              ) : null}
                              {pod.supportedActions.videoReload ? (
                                <Badge bg="secondary">Video</Badge>
                              ) : null}
                              {!pod.capabilities.length ? (
                                <Badge bg="light" text="dark">
                                  Legacy
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              className="pod-management-primary-button"
                              variant="primary"
                              onClick={() => setActionsPodName(pod.name)}
                            >
                              Actions
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          <div className="pod-management-log-panel">
            <div className="pod-management-panel-header">
              <h3 className="pod-management-panel-title">Recent Actions</h3>
              {auditFetching ? <Spinner animation="border" size="sm" /> : null}
            </div>
            <div className="p-3 border-bottom">
              <Form.Label className="pod-management-label">
                Filter By Pod
              </Form.Label>
              <Form.Select
                value={auditPodFilter}
                onChange={event => setAuditPodFilter(event.target.value)}
              >
                <option value="">All Pods</option>
                {pods.map(pod => (
                  <option key={pod.name} value={pod.name}>
                    {pod.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            {auditError ? (
              <Alert variant="danger" className="m-3">
                Unable to load action history.
              </Alert>
            ) : (
              <div className="pod-management-log-body">
                {auditLogs.length === 0 ? (
                  <div className="pod-management-empty-state">
                    No admin pod actions have been logged yet.
                  </div>
                ) : (
                  auditLogs.map(log => <AuditLogItem key={log.id} log={log} />)
                )}
              </div>
            )}
          </div>
        </div>

        <ConfigEditorModal
          pod={configPod}
          configuration={configPayload}
          form={configForm}
          isLoading={
            readConfigurationState.isFetching ||
            readCreativeConfigurationState.isFetching
          }
          isSaving={
            updateConfigurationState.isLoading ||
            updateCreativeConfigurationState.isLoading
          }
          show={Boolean(configPod)}
          onChange={updateConfigField}
          onHide={closeConfiguration}
          onSave={saveConfiguration}
        />
        <PodActionsModal
          pod={selectedActionsPod}
          activeAction={activeAction}
          show={Boolean(actionsPodName && selectedActionsPod)}
          onHide={() => setActionsPodName('')}
          onRunAction={runAction}
          onOpenConfiguration={openConfiguration}
        />
        </div>
      </PodManagementPageStyles>
    </>
  );
}
